import { fail } from "@homebound/activesupport";
import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCurrentUser } from "src/utils/hooks/useCurrentUser";
import { HBLoadingSpinner } from "~components";
import { UserType } from "~generated/graphqlTypes";
import { cleanValidationError } from "~utils";
import { createConfiguratorUrl } from "../../RouteUrls";
import { FlowStep } from "../configurator/stepTemplates";
import { useQrScan_SaveCheckoutConfigMutation } from "./QrScan.page.generated";

/**
 * QR Code Scan Landing Page
 *
 * This page handles QR code scans from physical locations (e.g., model homes).
 * Flow:
 * 1. User scans QR code → lands on /qr?c=p:5248&s=p:5650&room=interior
 * 2. If not authenticated → redirected to auth (handled by AnimatedRoutes)
 * 3. After auth → lands back here with preserved query params
 * 4. Creates/reuses checkout config for the structure
 * 5. Redirects to the specified room (e.g., /communities/p:5248/config/ccfg:3039/interior)
 */
export function QrScanPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser } = useCurrentUser();
  const [saveQRScanCheckoutConfig] = useQrScan_SaveCheckoutConfigMutation();

  const [communityId, structureId, roomLocationId] = ["c", "s", "r"].map((key) => searchParams.get(key));

  useEffect(() => {
    // Only run if we have user data loaded
    if (!currentUser) return;

    if (!structureId || !communityId) {
      fail("Invalid QR Code");
    }

    const handleQRScan = async () => {
      const result = await saveQRScanCheckoutConfig({
        variables: {
          input: {
            structureId,
            homeownerId: currentUser.type === UserType.Homeowner ? currentUser.id : undefined,
            reuseExistingConfig: true,
          },
        },
      });

      if (result.errors?.nonEmpty) {
        const cleanErrorMessage = cleanValidationError(result.errors.map((e) => e.message).join(", "));
        return fail(`Unable to create configuration: ${cleanErrorMessage}`);
      }

      const ccfgId = result.data?.saveCheckoutConfig?.checkoutConfig?.id;
      if (ccfgId) {
        // Redirect to the interior step for investor demo
        navigate(
          createConfiguratorUrl(
            communityId,
            ccfgId,
            FlowStep.Interior,
            roomLocationId ? "1" : undefined,
            roomLocationId ?? undefined,
          ),
        );
      }
    };

    void handleQRScan();
  }, [structureId, communityId, roomLocationId, currentUser, saveQRScanCheckoutConfig, navigate]);

  // Show loading state while processing
  return <HBLoadingSpinner />;
}
